import { useDispatch } from 'react-redux';
import { useMutation } from 'react-query';
import { urlWithParams } from '../../CommonUtils/urlWithParams';
import { authFetch } from '../../CommonUtils/authFetch';
import { successAppVariablesRequest } from '../ConsumerAreaActions';
import { updateConsumerAreaGlobalContext } from '../utils/updateConsumerAreaGlobalContext';

interface GetApplicationVariablesParams {
  clientId: string;
  completedWorkflow?: boolean;
}

const getApplicationVariables = ({
  clientId,
  completedWorkflow,
}: GetApplicationVariablesParams) => {
  return authFetch(
    urlWithParams('/Consumer/Consumer/GetApplicationVariables', {
      isPresentationMode: ADV.info.isPresentationMode,
      completedWorkflow,
      clientId,
    }),
  );
};

const useGetApplicationVariablesMutation = () => {
  return useMutation(getApplicationVariables);
};

interface Params {
  clientId: string;
  completedWorkflow?: boolean;
}

interface AppVariables {
  componentName: string | null;
  modulesToHide: string[];
  consumerAccessWorkflow: number;
}

export const useBootstrapConsumerArea = () => {
  const mutationBag = useGetApplicationVariablesMutation();

  const dispatch = useDispatch();

  const { mutateAsync: getAppVariables } = mutationBag;

  const bootstrap = async ({ clientId, completedWorkflow }: Params): Promise<AppVariables> => {
    const variables = await getAppVariables({ clientId, completedWorkflow });

    dispatch(successAppVariablesRequest(variables));

    const context = variables.ConsumerAreaContext;

    updateConsumerAreaGlobalContext({
      onboardingWorkflow: context.ConsumerAccessWorkflow,
      modulesToHide: context.ModulesToHide,
      optOuts: context.OptOuts,
      featureFlags: context.FeatureFlags,
      componentName: context.ComponentName,
    });

    return {
      componentName: context.ComponentName,
      modulesToHide: context.ModulesToHide,
      consumerAccessWorkflow: context.ConsumerAccessWorkflow,
    };
  };

  return {
    ...mutationBag,
    mutateAsync: bootstrap,
  };
};
